import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { CreditCard, ArrowLeft } from 'lucide-react';
import { apiRequest } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ArtistProfilePage() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArtistProfile();
  }, [artistId]);

  const fetchArtistProfile = async () => {
    try {
      setLoading(true);
      
      // Fetch artist details (no auth needed from public endpoint)
      const artistResponse = await fetch(`${BACKEND_URL}/api/admin/artists/public/list`);
      const artistsData = await artistResponse.json();
      
      if (artistResponse.ok && artistsData.artists) {
        const foundArtist = artistsData.artists.find(a => a.id === artistId);
        if (foundArtist) {
          setArtist(foundArtist);
        }
        
        // Fetch only digital cards
        const digitalCardsResponse = await apiRequest(`${BACKEND_URL}/api/admin/public/digital-cards`, {
          method: 'GET',
        });

        let allCards = [];
        
        if (digitalCardsResponse.ok) {
          const artistDigitalCards = (digitalCardsResponse.data.cards || [])
            .filter(card => card.artist_id === artistId && card.status === 'published')
            .map(card => ({ ...card, card_type: 'digital' }));
          allCards = [...allCards, ...artistDigitalCards];
        }
        
        setCards(allCards);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch artist profile:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading artist profile...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="text-center py-20">
          <p className="text-gray-600 mb-4">Artist not found</p>
          <Button onClick={() => navigate('/artist')} className="bg-navy text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Artists
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Banner Section */}
      <section 
        className="relative h-80"
        style={{
          backgroundImage: artist.banner_picture 
            ? `url(${BACKEND_URL}${artist.banner_picture})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="absolute top-4 left-4">
          <Button 
            onClick={() => navigate('/artist')}
            className="bg-white text-navy hover:bg-gray-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </section>

      {/* Artist Info Section */}
      <section className="px-4 -mt-20 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Profile Picture */}
              <div className="flex-shrink-0">
                {artist.profile_picture ? (
                  <img
                    src={`${BACKEND_URL}${artist.profile_picture}`}
                    alt={artist.name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-5xl border-4 border-white shadow-lg">
                    {artist.name.charAt(0)}
                  </div>
                )}
              </div>

              {/* Artist Details */}
              <div className="flex-1">
                <h1 className="text-4xl font-extrabold text-navy mb-2">{artist.name}</h1>
                
                {artist.short_intro && (
                  <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                    {artist.short_intro}
                  </p>
                )}

                <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-navy">{cards.length}</p>
                    <p className="text-sm text-gray-600">Digital Cards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cards Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-navy">Digital Cards</h2>
          </div>

          {cards.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl">
              <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Cards Available</h3>
              <p className="text-gray-600">This artist hasn't published any cards yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className="bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                >
                  {/* Card Image */}
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    {card.color_variants && card.color_variants.length > 0 && card.color_variants[0].front_design_url ? (
                      <img
                        src={`${BACKEND_URL}${card.color_variants[0].front_design_url}`}
                        alt={card.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <span className="text-gray-400">No Image</span>
                      </div>
                    )}
                  </div>

                  {/* Card Details */}
                  <div className="p-6">
                    {/* Available Colours & Prices */}
                    {card.color_variants && card.color_variants.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {card.color_variants.map((variant, idx) => (
                            <div key={idx} className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200">
                              <div
                                className="w-4 h-4 rounded-full border-2 border-gray-300"
                                style={{ backgroundColor: variant.colour_code || '#ccc' }}
                              />
                              <span className="text-xs font-semibold text-gray-700">{variant.colour_name || 'Color'}</span>
                              <span className="text-xs font-bold text-navy ml-1">${variant.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <h3 className="text-xl font-bold text-navy mb-2">{card.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{card.short_description}</p>
                    
                    <Button 
                      onClick={() => navigate(`/customize-card/${card.id}`)}
                      className="w-full bg-navy text-white hover:bg-navy/90 rounded-full py-3 flex items-center justify-center gap-2"
                    >
                      Order Now
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
